import { api } from "./apiClient";

const xhrPut = (url, body, headers = {}, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([name, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(name, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    xhr.onerror = () => reject(new Error("Direct S3 upload failed due to a network error."));
    xhr.onabort = () => reject(new Error("Direct S3 upload was aborted."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader("ETag") });
      } else {
        reject(new Error(`Direct S3 upload failed (${xhr.status}).`));
      }
    };
    xhr.send(body);
  });

const retryRequest = async (fn, attempts = 3) => {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt + 1 < attempts) {
        await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

const uploadMultipart = async (file, authorization, onProgress) => {
  const partSize = Number(authorization.part_size);
  const parts = authorization.parts || [];
  const loadedByPart = new Map();
  const completed = new Array(parts.length);
  let cursor = 0;

  const report = (partNumber, loaded) => {
    loadedByPart.set(partNumber, loaded);
    const totalLoaded = [...loadedByPart.values()].reduce((sum, value) => sum + value, 0);
    const percent = file.size ? Math.min(99, Math.round((totalLoaded / file.size) * 100)) : 99;
    onProgress?.(percent);
  };

  const worker = async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= parts.length) return;
      const part = parts[index];
      const partNumber = Number(part.part_number);
      const start = (partNumber - 1) * partSize;
      const end = Math.min(file.size, start + partSize);
      const blob = file.slice(start, end);
      const response = await xhrPut(part.url, blob, {}, (loaded) => report(partNumber, loaded));
      if (!response.etag) {
        throw new Error("S3 did not expose the multipart ETag. Check the bucket CORS ExposeHeaders setting.");
      }
      loadedByPart.set(partNumber, blob.size);
      report(partNumber, blob.size);
      completed[index] = { part_number: partNumber, etag: response.etag };
    }
  };

  const concurrency = Math.min(4, Math.max(1, parts.length));
  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  onProgress?.(100);
  return completed;
};

export const equipmentAPI = {
  list: (projectId, params = {}) =>
    api.get(`/projects/${projectId}/equipment`, { params }),

  overview: (projectId, dimension = "room") =>
    api.get(`/projects/${projectId}/equipment/overview`, {
      params: { dimension },
    }),

  options: (projectId) =>
    api.get(`/projects/${projectId}/equipment/options`),

  createOption: (projectId, kind, payload) =>
    api.post(`/projects/${projectId}/equipment/options/${kind}`, payload),

  getOne: (projectId, equipmentId) =>
    api.get(`/projects/${projectId}/equipment/${equipmentId}`),

  create: (projectId, payload) =>
    api.post(`/projects/${projectId}/equipment`, payload),

  update: (projectId, equipmentId, payload) =>
    api.patch(`/projects/${projectId}/equipment/${equipmentId}`, payload),

  archive: (projectId, equipmentId) =>
    api.delete(`/projects/${projectId}/equipment/${equipmentId}`),

  changeStatus: (projectId, equipmentId, payload) =>
    api.post(`/projects/${projectId}/equipment/${equipmentId}/status`, payload),

  sendCompletionNotification: (projectId, equipmentId) =>
    api.post(`/projects/${projectId}/equipment/${equipmentId}/completion-notification`),

  listProjectDocuments: (projectId) =>
    api.get(`/projects/${projectId}/equipment/documents`),

  listDocuments: (projectId, equipmentId) =>
    api.get(`/projects/${projectId}/equipment/${equipmentId}/documents`),

  uploadDocument: (projectId, equipmentId, formData) =>
    api.post(
      `/projects/${projectId}/equipment/${equipmentId}/documents/upload`,
      formData,
      {
        timeout: 120000,
        headers: { "Content-Type": "multipart/form-data" },
      },
    ),

  linkDocument: (projectId, equipmentId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/${equipmentId}/documents/link`,
      payload,
    ),

  reviewDocument: (projectId, equipmentId, linkId, payload) =>
    api.patch(
      `/projects/${projectId}/equipment/${equipmentId}/documents/${linkId}/review`,
      payload,
    ),

  unlinkDocument: (projectId, equipmentId, linkId) =>
    api.delete(
      `/projects/${projectId}/equipment/${equipmentId}/documents/${linkId}`,
    ),

  listDocumentRequirements: (projectId, equipmentId) =>
    api.get(
      `/projects/${projectId}/equipment/${equipmentId}/document-requirements`,
    ),

  addDocumentRequirement: (projectId, equipmentId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/${equipmentId}/document-requirements`,
      payload,
    ),

  updateDocumentRequirement: (projectId, requirementId, payload) =>
    api.patch(
      `/projects/${projectId}/equipment/document-requirements/${requirementId}`,
      payload,
    ),

  deleteDocumentRequirement: (projectId, requirementId) =>
    api.delete(
      `/projects/${projectId}/equipment/document-requirements/${requirementId}`,
    ),

  listTasks: (projectId, params = {}) =>
    api.get(`/projects/${projectId}/equipment/tasks`, { params }),

  listEquipmentTasks: (projectId, equipmentId) =>
    api.get(`/projects/${projectId}/equipment/${equipmentId}/tasks`),

  getTask: (projectId, taskId) =>
    api.get(`/projects/${projectId}/equipment/tasks/${taskId}`),

  createTask: (projectId, equipmentId, payload) =>
    api.post(`/projects/${projectId}/equipment/${equipmentId}/tasks`, payload),

  respondTask: (projectId, taskId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/tasks/${taskId}/response`,
      payload,
    ),

  addTaskComment: (projectId, taskId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/tasks/${taskId}/comments`,
      payload,
    ),

  uploadTaskEvidence: (projectId, taskId, formData) =>
    api.post(
      `/projects/${projectId}/equipment/tasks/${taskId}/evidence`,
      formData,
      {
        timeout: 120000,
        headers: { "Content-Type": "multipart/form-data" },
      },
    ),

  decideTask: (projectId, taskId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/tasks/${taskId}/decision`,
      payload,
    ),


  // Reusable checklist templates / designer
  listChecklistTemplates: (projectId, params = {}) =>
    api.get(`/projects/${projectId}/equipment/checklist-templates`, { params }),

  createChecklistTemplate: (projectId, payload) =>
    api.post(`/projects/${projectId}/equipment/checklist-templates`, payload),

  getChecklistTemplate: (projectId, templateId) =>
    api.get(`/projects/${projectId}/equipment/checklist-templates/${templateId}`),

  updateChecklistTemplate: (projectId, templateId, payload) =>
    api.patch(`/projects/${projectId}/equipment/checklist-templates/${templateId}`, payload),

  deleteChecklistTemplate: (projectId, templateId) =>
    api.delete(`/projects/${projectId}/equipment/checklist-templates/${templateId}`),

  createChecklistTemplateVersion: (projectId, templateId, payload = {}) =>
    api.post(`/projects/${projectId}/equipment/checklist-templates/${templateId}/versions`, payload),

  cloneChecklistTemplate: (projectId, templateId, payload) =>
    api.post(`/projects/${projectId}/equipment/checklist-templates/${templateId}/clone`, payload),

  getChecklistTemplateVersion: (projectId, versionId) =>
    api.get(`/projects/${projectId}/equipment/checklist-template-versions/${versionId}`),

  updateChecklistTemplateVersion: (projectId, versionId, payload) =>
    api.patch(`/projects/${projectId}/equipment/checklist-template-versions/${versionId}`, payload),

  publishChecklistTemplateVersion: (projectId, versionId, payload = {}) =>
    api.post(`/projects/${projectId}/equipment/checklist-template-versions/${versionId}/publish`, payload),



  listDesignedChecklists: (projectId, equipmentId) =>
    api.get(`/projects/${projectId}/equipment/${equipmentId}/designed-checklists`),

  assignDesignedChecklist: (projectId, equipmentId, payload) =>
    api.post(`/projects/${projectId}/equipment/${equipmentId}/designed-checklists`, payload),

  unlinkDesignedChecklist: (projectId, equipmentId, instanceId) =>
    api.delete(`/projects/${projectId}/equipment/${equipmentId}/designed-checklists/${instanceId}`),

  getDesignedChecklist: (projectId, instanceId) =>
    api.get(`/projects/${projectId}/equipment/designed-checklists/${instanceId}`),

  downloadDesignedChecklistPdf: (projectId, instanceId, mode = "filled") =>
    api.get(`/projects/${projectId}/equipment/designed-checklists/${instanceId}/pdf`, {
      params: { mode },
      responseType: "blob",
      timeout: 120000,
    }),


  listDesignedChecklistActivity: (projectId, instanceId, limit = 200) =>
    api.get(`/projects/${projectId}/equipment/designed-checklists/${instanceId}/activity`, { params: { limit } }),

  listEquipmentChecklistActivity: (projectId, equipmentId, limit = 200) =>
    api.get(`/projects/${projectId}/equipment/${equipmentId}/checklist-activity`, { params: { limit } }),

  saveDesignedChecklistResponses: (projectId, instanceId, payload) =>
    api.patch(`/projects/${projectId}/equipment/designed-checklists/${instanceId}/responses`, payload, {
      // Do not let an unresolved network request block a drawer close forever.
      timeout: 30000,
    }),

  designedChecklistSectionAction: (projectId, sectionId, payload) =>
    api.post(`/projects/${projectId}/equipment/designed-checklist-sections/${sectionId}/action`, payload),

  designedChecklistAction: (projectId, instanceId, payload) =>
    api.post(`/projects/${projectId}/equipment/designed-checklists/${instanceId}/action`, payload),


  linkDesignedChecklistAttachment: (projectId, instanceId, payload) =>
    api.post(`/projects/${projectId}/equipment/designed-checklists/${instanceId}/attachments/link`, payload),

  initiateDesignedChecklistAttachmentDirectUpload: (projectId, instanceId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/designed-checklists/${instanceId}/attachments/direct-upload/initiate`,
      payload,
    ),

  completeDesignedChecklistAttachmentDirectMultipart: (projectId, instanceId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/designed-checklists/${instanceId}/attachments/direct-upload/complete-multipart`,
      payload,
    ),

  abortDesignedChecklistAttachmentDirectMultipart: (projectId, instanceId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/designed-checklists/${instanceId}/attachments/direct-upload/abort`,
      payload,
    ),

  finalizeDesignedChecklistAttachmentDirectUpload: (projectId, instanceId, payload) =>
    api.post(
      `/projects/${projectId}/equipment/designed-checklists/${instanceId}/attachments/direct-upload/finalize`,
      payload,
    ),

  uploadDesignedChecklistAttachment: async (
    projectId,
    instanceId,
    file,
    { section_id = null, row_id = null, field_key = null } = {},
    onProgress,
  ) => {
    const base = {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      file_size: file.size,
      section_id,
      row_id,
      field_key,
    };
    onProgress?.(0);
    const initiated = await equipmentAPI.initiateDesignedChecklistAttachmentDirectUpload(
      projectId,
      instanceId,
      base,
    );
    const authorization = initiated.data;
    const finalizedScope = {
      ...base,
      section_id: authorization.section_id ?? section_id,
      row_id: authorization.row_id ?? row_id,
      field_key: authorization.field_key ?? field_key,
    };
    let parts = null;

    try {
      if (authorization.mode === "multipart") {
        parts = await uploadMultipart(file, authorization, onProgress);
      } else {
        await xhrPut(
          authorization.url,
          file,
          authorization.headers || {},
          (loaded) => {
            const percent = file.size ? Math.min(99, Math.round((loaded / file.size) * 100)) : 99;
            onProgress?.(percent);
          },
        );
        onProgress?.(100);
      }
    } catch (error) {
      if (authorization.mode === "multipart" && authorization.upload_id) {
        try {
          await equipmentAPI.abortDesignedChecklistAttachmentDirectMultipart(
            projectId,
            instanceId,
            {
              object_key: authorization.object_key,
              upload_id: authorization.upload_id,
            },
          );
        } catch (_) {
          // Best-effort cleanup; preserve the original upload error.
        }
      }
      throw error;
    }

    const finalizePayload = {
      ...finalizedScope,
      object_key: authorization.object_key,
    };

    if (authorization.mode === "multipart") {
      try {
        await equipmentAPI.completeDesignedChecklistAttachmentDirectMultipart(
          projectId,
          instanceId,
          {
            object_key: authorization.object_key,
            upload_id: authorization.upload_id,
            parts,
          },
        );
      } catch (completionError) {
        try {
          return await retryRequest(
            () =>
              equipmentAPI.finalizeDesignedChecklistAttachmentDirectUpload(
                projectId,
                instanceId,
                finalizePayload,
              ),
            3,
          );
        } catch (_) {
          try {
            await equipmentAPI.abortDesignedChecklistAttachmentDirectMultipart(
              projectId,
              instanceId,
              {
                object_key: authorization.object_key,
                upload_id: authorization.upload_id,
              },
            );
          } catch (__unused) {
            // Best effort only.
          }
          throw completionError;
        }
      }
    }

    return retryRequest(
      () =>
        equipmentAPI.finalizeDesignedChecklistAttachmentDirectUpload(
          projectId,
          instanceId,
          finalizePayload,
        ),
      3,
    );
  },

  reviewDesignedChecklistAttachment: (projectId, attachmentId, payload) =>
    api.patch(`/projects/${projectId}/equipment/checklist-attachments/${attachmentId}/review`, payload),

  deleteDesignedChecklistAttachment: (projectId, attachmentId) =>
    api.delete(`/projects/${projectId}/equipment/checklist-attachments/${attachmentId}`),

};
